import React, { useState } from 'react';

import { ExportIcon } from 'components/opsVisibility/components/inventoryHistoryV2/filterComponentTailwind/Icons';

import { useLedgerApi } from '../InventoryLedgerPreviewContext';

/**
 * Button that exports events to CSV
 *
 * @param {string} type - The type of export ('sku' or 'lot')
 * @param {string} skuOrLotId - The SKU or lot ID to export
 * @param {string} facilityId - The facility ID
 * @param {string} queryParams - Query parameters for the API call
 * @param {function} setNotification - Function to set notifications
 */
function ExportEventsButton({ companySourceId, facilityId, queryParams = '', setNotification, skuOrLotId, type }) {
    const ledgerApi = useLedgerApi();
    const [isExporting, setIsExporting] = useState(false);

    const handleExportClick = async () => {
        if (isExporting) return;

        setIsExporting(true);

        try {
            // Add pagination parameters to query string
            const paginationParams = 'pageNumber=0&pageSize=10000';
            const fullQueryParams = queryParams ? `${queryParams}&${paginationParams}` : paginationParams;

            // Call the API method that returns the response
            const res = await ledgerApi.exportEventCSV(type, skuOrLotId, companySourceId, facilityId, fullQueryParams);

            // Handle the blob response
            const blob = await res.blob();

            // Handle special case for IE
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob);
                setNotification({
                    text: `Exporting events for ${type} ${skuOrLotId}`,
                    level: 'success',
                });
                return;
            }

            // Create object URL
            const objectUrl = window.URL.createObjectURL(blob);

            // Create and trigger download link
            const link = document.createElement('a');
            document.body.appendChild(link);
            link.href = objectUrl;

            // Set file name
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            link.download = `inventory-events-${type}-${skuOrLotId}-${timestamp}.csv`;
            link.click();

            // Clean up
            document.body.removeChild(link);
            setTimeout(() => {
                if (window.URL && typeof window.URL.revokeObjectURL === 'function') {
                    window.URL.revokeObjectURL(objectUrl);
                }
            }, 200);

            // Show success notification
            setNotification({
                text: `Exporting events for ${type} ${skuOrLotId}`,
                level: 'success',
            });
        } catch (error) {
            setNotification({
                text: 'Error exporting events',
                level: 'error',
            });
            console.error('Export error:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <button
            aria-label="export events"
            className="p-1 rounded-full export-button-hover"
            onClick={handleExportClick}
            disabled={isExporting}
            title="Export Events"
        >
            {isExporting ? (
                <span className="w-5 h-5 spinner animate-spin inline-block text-black" />
            ) : (
                <ExportIcon className="h-5 w-5 text-black" />
            )}
        </button>
    );
}

export { ExportEventsButton };
